import React, { useContext, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { ReactComponent as CheckIcon } from '../../assets/images/icons/check_white.svg';
import { ReactComponent as GiftIcon } from '../../assets/images/icons/gift_outline_24.svg';
import { useHardwareFirstGiftableCart } from '../../lib/hardwareFirstGifting';
import { useLocale } from '../../contexts/LocaleContext';
import { formatHardwarePrice } from '../../lib/hardwareFirst';
import CheckoutContext from '../../lib/CheckoutContext';
import { cartActions } from '../../reducers/storeCart';
import GiftCardSheet from './GiftCardSheet';
import { GIFT_TERM_LABEL_KEYS } from './giftTermLabelKeys';
import HowGiftingWorksSheet from './HowGiftingWorksSheet';
import useHardwareFirstGiftPricing from './useHardwareFirstGiftPricing';
import optionStyles from '../StripeCheckout/GiftOptions.module.scss';
import styles from './GiftCheckbox.module.scss';

export default function GiftCheckbox() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cart } = useContext(CheckoutContext);
  const giftable = useHardwareFirstGiftableCart(cart);
  const isGift = 'isGift' in cart && !!cart.isGift;
  const giftTermMonths = 'giftTermMonths' in cart ? cart.giftTermMonths : undefined;
  const { planTerms } = useHardwareFirstGiftPricing();
  const formatters = useLocale();
  const labelId = useId();

  if (!giftable) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.check}>
        <GiftIcon className={styles.icon} />
        <span className={styles.label}>
          <span id={labelId}>{t('hardware_first_gift_checkbox_label')}</span>
          <span className={styles.sub}>
            {t('hardware_first_gift_checkbox_sub')}{' '}
            <HowGiftingWorksSheet
              trigger={
                <button className={styles.learnMore} type="button">
                  {t('learn_more')}
                </button>
              }
            />
          </span>
        </span>
        <button
          aria-checked={isGift}
          aria-labelledby={labelId}
          className={classNames(styles.box, { [styles.on]: isGift })}
          onClick={() => dispatch(cartActions.setIsGift(!isGift))}
          role="checkbox"
          type="button"
        >
          {isGift && <CheckIcon />}
        </button>
      </div>
      {isGift && (
        <div aria-label={t('hardware_first_gift_term_group_label')} className={styles.tiles} role="radiogroup">
          {planTerms.map(({ months, introAmountInCents, listAmountInCents }) => (
            <label className={classNames(styles.tile, { [optionStyles.on]: giftTermMonths === months })} key={months}>
              <input
                checked={giftTermMonths === months}
                className={optionStyles.radioInput}
                name={`${labelId}-term`}
                onChange={() => dispatch(cartActions.setGiftTermMonths(months))}
                type="radio"
              />
              <span className={styles.tileName}>{t(GIFT_TERM_LABEL_KEYS[months])}</span>
              <span className={styles.tileSub}>
                <b className={styles.tileAmount}>
                  {t('positive_amount', { amount: formatHardwarePrice(introAmountInCents, formatters) })}
                </b>
                {listAmountInCents > introAmountInCents && (
                  <s className={styles.was}>{formatHardwarePrice(listAmountInCents, formatters)}</s>
                )}
              </span>
            </label>
          ))}
          <p className={styles.fine}>{t('hardware_first_gift_checkbox_fine_print')}</p>
          <GiftCardSheet
            trigger={
              <button className={classNames(styles.linkButton, styles.cardLink)} type="button">
                {t('hardware_first_gift_card_link')}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
}
